import type { HomeQueueData, QueuePart } from './homeApi';
import { signed } from '../components/Objectives/planFormat';

/** Home's attention list (#569), pure: the rail badge and Home's Needs you read the same rows. */

/** Where a Needs you row opens. */
export type NeedsYouTarget =
  | { to: 'review'; threadId: string }
  | { to: 'activity'; agentId: string | null }
  | { to: 'wall'; agentId: string }
  | { to: 'plan'; agentId: string }
  | { to: 'usage'; agentId: string }
  | { to: 'thread'; threadId: string }
  | { to: 'definitions' }
  | { to: 'lessons' };

export interface NeedsYouRow {
  key: string;
  kind: 'numbers' | 'proposal' | 'contest' | 'blocked' | 'definition' | 'lesson' | 'promise' | 'briefing';
  title: string; detail: string | null; meta: string | null; action: string; target: NeedsYouTarget;
}

const items = <T,>(part: QueuePart<T> | undefined | null): T[] => (Array.isArray(part?.items) ? part!.items : []);

/**
 * An agent headline is "metric: value · delta · pace · the reading"
 * (agents.runner._headline): the first segment is what the row IS, the rest is detail.
 */
export function splitHeadline(headline: string): { lead: string; detail: string | null } {
  const [lead, ...rest] = String(headline || '').split(' · ');
  return { lead: lead || String(headline || ''), detail: rest.join(' · ') || null };
}

/** "From A, B and C" — where the lessons came from, the first three named. */
export function fromSources(sources: Array<string | null | undefined>): string | null {
  const names = Array.from(new Set(sources.filter((x): x is string => !!x)));
  if (!names.length) return null;
  const shown = names.slice(0, 3);
  const more = names.length - shown.length;
  if (more) return `From ${shown.join(', ')} and ${more} more`;
  return `From ${shown.length > 1 ? `${shown.slice(0, -1).join(', ')} and ${shown[shown.length - 1]}` : shown[0]}`;
}

const day = (iso?: string | null) => { const d = new Date(String(iso || '')); return Number.isNaN(d.getTime()) ? 'soon' : d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); };

/**
 * Everything on Home that is waiting on the viewer, in the order Home lists it.
 * The rail's Home badge is this list's length, so the badge and the Needs you
 * header can never disagree.
 */
export function needsYouRows(queue: HomeQueueData | null | undefined): NeedsYouRow[] {
  if (!queue) return [];
  const rows: NeedsYouRow[] = [];
  for (const r of items(queue.numbers_to_fix)) {
    rows.push({ key: `numbers:${r.id}`, kind: 'numbers', action: 'Open review', target: { to: 'review', threadId: r.thread_id },
      title: `${r.title} has ${r.count} number${r.count === 1 ? '' : 's'} to fix before it goes out`, detail: r.detail || null, meta: r.deck || null });
  }
  for (const r of items(queue.needs_your_say)) {
    if (r.kind === 'definition') continue;
    const { lead, detail } = splitHeadline(r.title);
    if (r.kind === 'proposal' && r.plan && r.agent_id) {
      // the objective's current plan (#572): its Plan tab is where it is approved
      const exp = r.plan.expected;
      rows.push({ key: `plan:${r.id}`, kind: 'proposal', action: 'Review', target: { to: 'plan', agentId: r.agent_id },
        title: `Next plan ready for ${r.plan.objective || lead}`,
        detail: [`${r.plan.actions} action${r.plan.actions === 1 ? '' : 's'}`, exp ? `expected ${signed(exp.value, exp.unit)}` : null].filter(Boolean).join(' · '),
        meta: r.post_ids.length > 1 ? `${r.post_ids.length} runs` : null });
      continue;
    }
    // a row names ONE agent's decision: Activity filtered to that agent reaches it
    rows.push({ key: `${r.kind}:${r.id}`, kind: r.kind, action: 'Review', target: { to: 'activity', agentId: r.agent_id || null },
      title: r.kind === 'contest' ? `Contested: ${lead}` : `Proposal to approve: ${lead}`, detail,
      meta: r.post_ids.length > 1 ? `${r.post_ids.length} runs` : null });
  }
  for (const r of items(queue.blocked_actions)) {
    if (r.kind === 'check') {
      // a checked action a guardrail stopped (#574): the owner's decision, made on the objective's Where it's used tab
      rows.push({ key: `blocked:${r.id}`, kind: 'blocked', action: 'Decide', target: { to: 'usage', agentId: r.agent_id },
        title: `An action was blocked: ${r.action ?? 'an action below your evidence bar'}`,
        // a person is the signed caller; an agent is always shown with the person who checked on its behalf
        detail: [r.actor?.kind === 'agent' ? `${r.actor.name || 'An agent'} (checked by ${r.caller?.name || 'someone'})` : r.caller?.name,
          r.reason ? r.reason.replace(/^breaks guardrail: /, 'breaks ') : null].filter(Boolean).join(' · ') || null,
        meta: r.objective || r.agent_name || null });
      continue;
    }
    rows.push({ key: `blocked:${r.id}`, kind: 'blocked', action: 'Details', target: { to: 'wall', agentId: r.agent_id },
      title: `An action was blocked${r.agent_name ? ` · ${r.agent_name}` : ''}`, detail: r.reason || null, meta: null });
  }
  for (const r of items(queue.needs_your_say)) {
    if (r.kind !== 'definition') continue;
    rows.push({ key: `definition:${r.id}`, kind: 'definition', action: 'Review', target: { to: 'definitions' },
      title: `Definition to review: ${r.title}`, detail: null, meta: null });
  }
  const lessons = items(queue.lessons);
  if (lessons.length) {
    // one row for all of them (#579): they are reviewed together, in Knowledge › Needs review
    rows.push({ key: 'lessons', kind: 'lesson', action: 'Review', target: { to: 'lessons' },
      title: `${lessons.length} new lesson${lessons.length === 1 ? '' : 's'} to review`, detail: fromSources(lessons.map((r) => r.detail)), meta: null });
  }
  for (const p of items(queue.promises)) {
    const status = p.status === 'open' ? 'checking' : p.status;
    rows.push({ key: `promise:${p.id}`, kind: 'promise', action: 'Open',
      target: p.delivery?.channel === 'chat' ? { to: 'thread', threadId: p.delivery.ref } : { to: 'wall', agentId: p.agent_id },
      title: p.question,
      detail: p.status === 'delivered' ? `delivered · ${p.delivery?.channel || 'wall'}` : p.status === 'open' ? `due ${day(p.due_at)}` : null,
      meta: status });
  }
  for (const b of items(queue.briefings)) {
    rows.push({ key: `briefing:${b.id}`, kind: 'briefing', action: 'Read', target: { to: 'wall', agentId: b.agent_id },
      title: `Today's briefing · ${b.title}`, detail: b.summary, meta: null });
  }
  return rows;
}

/** The parts Needs you reads that could not be loaded, so an empty list is never read as "caught up". */
export function needsYouNotes(queue: HomeQueueData | null | undefined): string[] {
  if (!queue) return [];
  return [queue.numbers_to_fix, queue.needs_your_say, queue.blocked_actions, queue.lessons, queue.promises, queue.briefings]
    .map((part) => part?.note).filter((note): note is string => !!note);
}
